import * as THREE from "three";

export type ToothRotationMap = Record<
  string,
  { x: number; y: number; z: number }
>;

export interface ToothInteractionState {
  toothObjectMap: Map<string, THREE.Object3D>;
  toothSelectionProxyMap: Map<string, THREE.Mesh>;
  selectableToothObjects: THREE.Object3D[];
}

export function createToothInteractionState(): ToothInteractionState {
  return {
    toothObjectMap: new Map<string, THREE.Object3D>(),
    toothSelectionProxyMap: new Map<string, THREE.Mesh>(),
    selectableToothObjects: [],
  };
}

export function createSelectionProxy(
  state: ToothInteractionState,
  anatomyGroup: THREE.Object3D,
  toothId: string
): THREE.Mesh {
  const proxy = new THREE.Mesh(
    new THREE.BoxGeometry(1, 1, 1),
    new THREE.MeshBasicMaterial({
      color: 0x22c55e,
      transparent: true,
      opacity: 0,
      depthWrite: false,
      wireframe: true,
    })
  );

  proxy.name = `tooth-selection-proxy-${toothId}`;
  proxy.visible = false;
  proxy.renderOrder = 999;

  proxy.userData = {
    ...proxy.userData,
    type: "tooth",
    toothId,
    isSelectionProxy: true,
  };

  state.toothSelectionProxyMap.set(toothId, proxy);
  state.selectableToothObjects.push(proxy);
  anatomyGroup.add(proxy);

  return proxy;
}

export function getOrCreateSelectionProxy(
  state: ToothInteractionState,
  anatomyGroup: THREE.Object3D,
  toothId: string
): THREE.Mesh {
  return (
    state.toothSelectionProxyMap.get(toothId) ??
    createSelectionProxy(state, anatomyGroup, toothId)
  );
}

export function registerToothObject(
  state: ToothInteractionState,
  anatomyGroup: THREE.Object3D,
  toothId: string,
  rootObject: THREE.Object3D
): void {
  state.toothObjectMap.set(toothId, rootObject);
  updateSelectionProxyForObject(state, anatomyGroup, toothId, rootObject);
}

export function unregisterToothObject(
  state: ToothInteractionState,
  toothId: string
): void {
  state.toothObjectMap.delete(toothId);
}

export function updateSelectionProxyForObject(
  state: ToothInteractionState,
  anatomyGroup: THREE.Object3D,
  toothId: string,
  rootObject: THREE.Object3D
): void {
  const proxy = getOrCreateSelectionProxy(state, anatomyGroup, toothId);

  rootObject.updateWorldMatrix(true, true);

  const box = new THREE.Box3().setFromObject(rootObject);
  if (box.isEmpty()) {
    proxy.visible = false;
    return;
  }

  const size = new THREE.Vector3();
  const center = new THREE.Vector3();
  box.getSize(size);
  box.getCenter(center);

  proxy.position.copy(center);
  proxy.scale.set(
    Math.max(size.x * 1.08, 0.5),
    Math.max(size.y * 1.08, 0.5),
    Math.max(size.z * 1.08, 0.5)
  );
}

export function refreshAllSelectionProxies(
  state: ToothInteractionState,
  anatomyGroup: THREE.Object3D
): void {
  state.toothObjectMap.forEach((rootObject, toothId) => {
    updateSelectionProxyForObject(state, anatomyGroup, toothId, rootObject);
  });
}

export function refreshSelectionProxyVisibility(
  state: ToothInteractionState,
  selectedToothId: string | null
): void {
  state.toothSelectionProxyMap.forEach((proxy, toothId) => {
    const isSelected = toothId === selectedToothId;
    proxy.visible = isSelected;

    const material = proxy.material;
    if (material instanceof THREE.MeshBasicMaterial) {
      material.opacity = isSelected ? 0.35 : 0;
    }
  });
}

export function applyToothRotations(
  state: ToothInteractionState,
  toothRotations?: ToothRotationMap
): void {
  if (!toothRotations) {
    return;
  }

  state.toothObjectMap.forEach((rootObject, toothId) => {
    const userRot = toothRotations[toothId];
    if (!userRot) {
      return;
    }

    rootObject.rotation.x += THREE.MathUtils.degToRad(userRot.x);
    rootObject.rotation.y += THREE.MathUtils.degToRad(userRot.y);
    rootObject.rotation.z += THREE.MathUtils.degToRad(userRot.z);
  });
}

export function applyToothRotationsAndRefreshProxies(
  state: ToothInteractionState,
  anatomyGroup: THREE.Object3D,
  toothRotations?: ToothRotationMap
): void {
  applyToothRotations(state, toothRotations);
  refreshAllSelectionProxies(state, anatomyGroup);
}

export function findToothIdFromIntersectionObject(
  object: THREE.Object3D | null | undefined
): string | null {
  let current: THREE.Object3D | null | undefined = object;

  while (current) {
    const toothId = current.userData?.toothId;
    if (typeof toothId === "string" && toothId.length > 0) {
      return toothId;
    }
    current = current.parent;
  }

  return null;
}

export function disposeToothInteractionState(
  state: ToothInteractionState,
  anatomyGroup?: THREE.Object3D
): void {
  state.toothSelectionProxyMap.forEach((proxy) => {
    if (proxy.parent) {
      proxy.parent.remove(proxy);
    } else if (anatomyGroup) {
      anatomyGroup.remove(proxy);
    }

    if (proxy.geometry) {
      proxy.geometry.dispose();
    }

    const material = proxy.material;
    if (Array.isArray(material)) {
      material.forEach((m) => m.dispose());
    } else {
      material.dispose();
    }
  });

  state.toothObjectMap.clear();
  state.toothSelectionProxyMap.clear();
  state.selectableToothObjects.length = 0;
}